/**
 * useAIBot — encapsulates ALL state + handlers for a background AI bot
 * job (AI Book Reviewer, AI Book Discussion). Both used to be 60-line
 * duplicate handler blocks in AdminSettings.jsx that differed only in
 * endpoint paths, polling field name, and human-readable labels.
 *
 * Extracted from AdminSettings.jsx (2026-05-30) as part of the P4
 * refactor pass to get that page below the 750-line health threshold.
 *
 * Usage:
 *   const reviewer = useAIBot({
 *     endpointBase: 'ai-reviewer',
 *     pollField: 'reviews_generated',
 *     successLabel: 'AI reviews',
 *     kindLabel: 'AI Reviews',
 *   });
 *   // reviewer.status, reviewer.generating, reviewer.toggling,
 *   // reviewer.toggle(), reviewer.generate(), reviewer.refresh()
 *
 * The hook fetches status on mount and exposes idempotent toggle / generate
 * actions. Generate kicks off the backend job (returns immediately with
 * HTTP 202) and then polls /generate-status every 1-1.5s until the job
 * resolves to 'done' or 'error', or 5 minutes elapse.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useToast } from './use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const useAIBot = ({
  endpointBase,    // 'ai-reviewer' | 'ai-discussion'
  pollField,       // 'reviews_generated' | 'messages_generated'
  successLabel,    // 'AI reviews' | 'AI chat messages'
  kindLabel,       // 'AI Reviews' | 'AI Discussion'
  enabled = true,  // skip fetch when user is not admin
}) => {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!enabled) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/${endpointBase}/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch {
      /* not available — leave status null so panel can render empty state */
    }
  }, [endpointBase, enabled]);

  // Poll the backend job until it's done. Backend is parallelised so most
  // runs finish in 8-15s — we use a fast first check then 1.5s cadence.
  const pollGenerationStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    const start = Date.now();
    const maxWait = 5 * 60 * 1000; // 5 minute hard cap
    let firstPoll = true;
    while (Date.now() - start < maxWait) {
      await new Promise((r) => setTimeout(r, firstPoll ? 1000 : 1500));
      firstPoll = false;
      try {
        const res = await axios.get(`${API}/${endpointBase}/generate-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const row = res.data || {};
        if (row.status === 'done') {
          const count = row[pollField] || 0;
          if (row.warning) {
            toast({
              title: `Generated ${count} ${successLabel} (with warnings)`,
              description: row.warning,
              variant: 'destructive',
            });
          } else if (count === 0) {
            toast({
              title: `Generated 0 ${successLabel}`,
              description: 'Nothing was produced. Try again or check the LLM key balance.',
              variant: 'destructive',
            });
          } else {
            toast({
              title: `Generated ${count} ${successLabel}`,
              description: 'Successfully completed in the background.',
            });
          }
          fetchStatus();
          return;
        }
        if (row.status === 'error') {
          toast({
            title: `${kindLabel} failed`,
            description: row.warning || 'Background job hit an error.',
            variant: 'destructive',
          });
          return;
        }
        // status === 'running' → keep polling
      } catch {
        // Transient poll error — keep trying until maxWait
      }
    }
    toast({
      title: `${kindLabel} timed out`,
      description: 'Generation is still running on the server. Check back in a minute.',
      variant: 'destructive',
    });
  }, [endpointBase, pollField, successLabel, kindLabel, toast, fetchStatus]);

  const generate = useCallback(async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      // Backend returns immediately (HTTP 202 + job_id) so we never hit
      // the prod ingress timeout. We then poll /generate-status.
      await axios.post(`${API}/${endpointBase}/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30_000,
      });
      toast({
        title: `${kindLabel} started`,
        description: 'Generating in the background — usually finishes in under a minute.',
      });
      await pollGenerationStatus();
    } catch (error) {
      toast({
        title: `${kindLabel} failed`,
        description: error.response?.data?.detail || error.message || 'Could not start generation',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  }, [endpointBase, kindLabel, pollGenerationStatus, toast]);

  const toggle = useCallback(async () => {
    setToggling(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/${endpointBase}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({
        title: res.data.enabled ? `${kindLabel} Enabled` : `${kindLabel} Disabled`,
        description: res.data.message,
      });
      fetchStatus();
    } catch {
      toast({ title: 'Error', description: `Failed to toggle ${kindLabel.toLowerCase()}`, variant: 'destructive' });
    } finally {
      setToggling(false);
    }
  }, [endpointBase, kindLabel, fetchStatus, toast]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  return { status, generating, toggling, toggle, generate, refresh: fetchStatus };
};
